import { useGetAuditNeededCalls, useGetIssueContexts } from '@/apis/agency/systemSentiment/hook';
import ReportFlagIcon from '@/assets/icons/ReportFlagIcon';
import DataTable from '@/components/Common/DataTable';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import Search from '@/components/Common/Search';
import Select from '@/components/Common/Select';
import Tooltip from '@/components/Common/Tooltip';
import { formatDate, formatDuration, titleCase } from '@/utils/helperFunctions';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { Link, Outlet } from 'react-router-dom';

type ReportStatusFilter = 'reported' | 'all' | 'unreported';

const REPORT_STATUS_OPTIONS: { value: ReportStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'reported', label: 'Reported' },
  { value: 'unreported', label: 'Unreported' }
];

export default function CallNeedingAudit({ filters }: { filters: Record<string, any> }) {
  const { currentPage: page, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [searchText, setSearchText] = useState<string | null>(null);
  const { control, watch } = useForm<{ issues: string[]; reportStatus: ReportStatusFilter }>({
    defaultValues: { issues: [], reportStatus: 'all' }
  });
  const selectedIssues = watch('issues');
  const reportStatus = watch('reportStatus');

  const { data: issueContexts } = useGetIssueContexts();
  const issueOptions = useMemo(() => {
    if (!issueContexts?.data?.length) return [];

    const seen = new Set<string>();
    return issueContexts.data.flatMap((group) =>
      (group.tags ?? []).flatMap((tag) => {
        if (seen.has(tag.label)) return [];
        seen.add(tag.label);
        return [
          {
            value: tag.label,
            label: tag.label,
            category: group.label
          }
        ];
      })
    );
  }, [issueContexts?.data]);

  const callId = searchText?.trim() || undefined;
  const issues = useMemo(() => (selectedIssues ?? []).filter(Boolean), [selectedIssues]);

  useEffect(() => {
    resetPage();
  }, [callId, issues, reportStatus, resetPage]);

  const { data: CallLogs, isLoading } = useGetAuditNeededCalls({
    page,
    per_page: perPage,
    filter: {
      ...filters,
      interaction_type_eq: 0,
      ...(callId ? { call_id: callId } : {}),
      ...(issues.length ? { issues } : {}),
      ...(reportStatus !== 'all' ? { report_status: reportStatus } : {})
    }
  });

  const memoizedCallLogs = useMemo(() => CallLogs?.data, [CallLogs?.data]);

  return (
    <Box border='1px solid #ECECEC' borderRadius={2} p={2} mb={3} bgcolor='#fff'>
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2} mb={2}>
        <Typography fontSize={20} fontWeight={500}>
          Calls Needing Audit
        </Typography>
        <Stack direction='row' alignItems='center' gap={1.5} flexShrink={0}>
          <Box width={220}>
            <Search
              placeholder='Search by Call Id'
              searchText={searchText}
              setSearchText={setSearchText}
              size='small'
              fullWidth
            />
          </Box>
          <Box width={220}>
            <Select
              name='reportStatus'
              label='Report Status'
              control={control}
              sortOrder='none'
              options={REPORT_STATUS_OPTIONS}
              size='small'
              sx={{ background: '#fff', maxHeight: '40px', width: '100%' }}
            />
          </Box>
          <Box width={220}>
            <Select
              name='issues'
              label='Filter by Issues'
              control={control}
              multiple
              visibleCount={0}
              sortOrder='none'
              options={issueOptions}
              size='small'
              sx={{ background: '#fff', maxHeight: '40px', width: '100%' }}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 360 }
                }
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <DataTable
        data={memoizedCallLogs}
        isLoading={isLoading}
        columns={[
          {
            header: 'Call ID',
            size: 50,
            accessorKey: 'interaction_id',
            cell: (info) => {
              const { interaction_id, report_call: report } = info.row.original;
              return (
                <Box display='flex' alignItems='center' gap={1}>
                  <Typography noWrap>CALL-{interaction_id}</Typography>
                  {report?.description && (
                    <Tooltip title={report.description}>
                      <Box display='flex' sx={{ flexShrink: 0, lineHeight: 0 }}>
                        <ReportFlagIcon />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            }
          },
          { header: 'Agent', accessorKey: 'agent_name', size: 150 },
          { header: 'Business', accessorKey: 'business_name', size: 150 },
          {
            header: 'Issues',
            size: 150,
            cell: (info) => getColumnBadges(info.row.original.audit_issues ?? {}, '#ECECEC', '#000')
          },
          {
            header: 'Duration',
            size: 120,
            accessorKey: 'duration',
            cell: (info) => <Typography noWrap>{formatDuration(info.row.original.duration)}</Typography>
          },
          {
            header: 'Date',
            size: 150,
            accessorKey: 'created_at',
            cell: (info) => <Typography noWrap>{formatDate(info.row.original.created_at)}</Typography>
          },
          {
            header: 'Action',
            size: 150,
            cell: (info) => (
              <Link to={String(info.row.original.interaction_id)}>
                <IconButton>
                  <MdOutlineRemoveRedEye color='#777777' />
                </IconButton>
              </Link>
            )
          }
        ]}
      />

      <Pagination
        totalPages={CallLogs?.meta?.total_pages || 1}
        totalCount={CallLogs?.meta?.total_count ?? 0}
        currentPage={page}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <Outlet />
    </Box>
  );
}

const getColumnBadges = (
  data: { label: string; description?: string }[] | string | string[],
  bgColor: string,
  color: string
) => {
  const Badge = ({ label, description }: { label: string; description?: string }) => {
    if (!label) return null;

    const badge = (
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          padding: '1px 12px',
          fontWeight: 400,
          color: color,
          backgroundColor: bgColor,
          borderRadius: '100px',
          whiteSpace: 'nowrap',
          cursor: description ? 'pointer' : 'default'
        }}
      >
        {titleCase(label)}
      </Typography>
    );

    if (!description) return badge;

    return <Tooltip title={description}>{badge}</Tooltip>;
  };

  const TooltipComponent = ({ items }: { items: { label: string; description?: string }[] }) => (
    <Box display='flex' flexWrap='wrap' gap={0.6} maxWidth={200}>
      {items.map((item, idx) => (
        <Box key={idx}>
          <Badge label={item.label} description={item.description} />
        </Box>
      ))}
    </Box>
  );

  let normalized: { label: string; description?: string }[] = [];

  if (typeof data === 'string') {
    normalized = [{ label: data }];
  } else if (Array.isArray(data)) {
    if (typeof data[0] === 'string') {
      normalized = (data as string[]).map((d) => ({ label: d }));
    } else {
      normalized = data as { label: string; description?: string }[];
    }
  }

  if (!normalized.length) return null;

  return normalized.length > 1 ? (
    <Box display='flex' flexDirection='row' gap={0.8}>
      <Badge label={normalized[0].label} description={normalized[0].description} />
      <Tooltip title={<TooltipComponent items={normalized.slice(1)} />} bgColor='white'>
        <Typography
          color={color}
          fontSize='12px'
          fontWeight={400}
          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          +{normalized.length - 1}
        </Typography>
      </Tooltip>
    </Box>
  ) : (
    <Badge label={normalized[0].label} description={normalized[0].description} />
  );
};
